import React, { useCallback, useState, useMemo } from "react";
import { ICrewPairingPositionMap, CrewPairingPositionGrouped } from "src/redux/reducers/CrewScheduleReducer";
import classNames from "classnames";
import { Theme, useTheme, Popover, MenuItem, alpha } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Grid } from "@mui/material";
import { IconButton } from "../../../../IconButton/IconButton";
import { useSelector, useDispatch } from "react-redux";
import { updatePairingEditor } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { IFullPairingPosition, isIFullPairingPosition, OpenFlyingStatusType } from "@vmosolutions/oc-interfaces";
import _ from "lodash";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface ICrewPairingActionsProps {
  id: string;
  crewMembersOnPairing: ICrewPairingPositionMap;
  openFlyingDisplayIndex: number; // Used to dertermine which assigned/unassigned position in the view has it's open flying controls visible (if any)
  setOpenFlyingDisplayIndex: React.Dispatch<React.SetStateAction<number>>; // Pass selected openFlying position Id to parent.
}

const CrewPairingActionsListStyle = (theme: Theme): any => ({
  height: "100%",
  overflow: "hidden",
  position: "relative",
  flexWrap: "nowrap",
  "& .menu_container": {
    display: "flex",
    backgroundColor: theme.custom.timeline.webglCanvas.evenRow,
    borderTop: "6px solid #383B3D",
    height: "50px",
    alignItems: "flex-start",
    justifyItems: "center",
    "& .menu_btn": {
      margin: "5px",
      padding: 0,
      borderRadius: "3px",
      "& svg": { fill: theme.palette.grey[600] },
      "&:hover svg": { fill: theme.palette.action.active },
    },
  },
});

const CrewPairingPopupStyle = (theme: Theme): any => ({
  boxShadow: theme.shadows[1],
  borderRadius: "3px",
  backgroundColor: alpha(theme.palette.common.black, 0.85),
  "& .event_popover_menu": {
    padding: 0,
    "& li": {
      fontSize: 12,
      "&:hover": { color: theme.palette.primary.contrastText },
    },
  },
});

export const CrewPairingActionsList = (props: ICrewPairingActionsProps): JSX.Element => {
  const { crewMembersOnPairing, openFlyingDisplayIndex, setOpenFlyingDisplayIndex } = props;
  const dispatch = useDispatch();
  const theme: Theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [posIndex, setPosIndex] = useState(null);
  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[props.id]);

  const fullPositions: IFullPairingPosition[] = useMemo(
    () => storedPairing?.positions ?? [],
    [storedPairing?.positions]
  );

  // Remove crew member from available slot.
  const handleEmptyCrewSlot = useCallback((): void => {
    const positions = _.cloneDeep(fullPositions);
    positions[posIndex].crew = null;
    positions[posIndex].openFlyingStatus = OpenFlyingStatusType.OpenNonVisible;
    dispatch(updatePairingEditor(props.id, { positions: positions }));
    setAnchorEl(null);
  }, [storedPairing, fullPositions, posIndex]);

  // Remove the entire crew slot.
  const handleRemoveCrewSlot = useCallback((): void => {
    const positions = _.cloneDeep(fullPositions);
    positions.splice(posIndex, 1);
    dispatch(updatePairingEditor(props.id, { positions: positions }));
    setAnchorEl(null);
  }, [storedPairing, fullPositions, posIndex]);

  // Toggle the Open Flying controls for this position
  const handleToggleOpenFlyingDisplay = useCallback((): void => {
    setOpenFlyingDisplayIndex((prev: number) => (prev > -1 && prev === posIndex ? -1 : posIndex));
    setAnchorEl(null);
  }, [posIndex, setOpenFlyingDisplayIndex]);

  const handlePopoverOpen = useCallback(
    (posIndex: number) =>
      (event: any): void => {
        setAnchorEl(event.currentTarget);
        setPosIndex(posIndex);
      },
    []
  );

  const handlePopoverClose = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  return (
    <>
      <Grid sx={CrewPairingActionsListStyle(theme)} container direction="column">
        {Object.keys(crewMembersOnPairing).map((crewPositionStr: string, index: number) => {
          const crewMembers = crewMembersOnPairing[crewPositionStr];
          return (
            <div key={index}>
              {crewMembers.map((crewMember: CrewPairingPositionGrouped, index: number) => {
                const posIndex = crewMember.positionIndex;
                const key = isIFullPairingPosition(crewMember)
                  ? `${crewMember.position.title}-${index}`
                  : crewMember._id;
                return (
                  <div
                    className={classNames("menu_container", isIFullPairingPosition(crewMember) && "empty")}
                    key={key}
                  >
                    <IconButton
                      onClick={handlePopoverOpen(posIndex)}
                      className="menu_btn"
                      id="edit_pairing_event_menu_btn"
                      aria-label="edit pairing event"
                      size="large"
                    >
                      <MenuIcon />
                    </IconButton>
                  </div>
                );
              })}
            </div>
          );
        })}
      </Grid>
      <Popover
        classes={{ paper: CrewPairingPopupStyle(theme) }}
        open={anchorEl ? true : false}
        onClose={handlePopoverClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
      >
        {
          <div className="event_popover_menu">
            <MenuItem onClick={() => handleEmptyCrewSlot()}>Empty Crew Slot</MenuItem>
            <MenuItem onClick={() => handleRemoveCrewSlot()}>Remove Crew Slot</MenuItem>
            <MenuItem onClick={() => handleToggleOpenFlyingDisplay()}>{`${
              posIndex === openFlyingDisplayIndex ? "Hide" : "Show"
            } Open Flying Selector`}</MenuItem>
          </div>
        }
      </Popover>
    </>
  );
};
